<template>
  <el-dialog :model-value="true" class="repair-bulk-merge-dialog" title="批量合并回货" fullscreen :close-on-click-modal="false" :before-close="beforeClose" @closed="$emit('close')">
    <div ref="bulkContent" v-loading="saving" class="bulk-merge">
      <el-alert type="info" :closable="false" title="先准备成品，再勾选来源分配。允许拆分来源和部分回货；应用后仍需保存回货批次。已有记录移出本次分配时恢复原状，不会删除记录。"></el-alert>
      <div class="allocation-summary">
        <span>本批可处理 <b>{{ summary.capacity }}</b> 件</span>
        <span>已分配（含本批已有） <b>{{ summary.allocated }}</b> 件</span>
        <span>剩余可分配 <b>{{ summary.remaining }}</b> 件</span>
        <span>目标成品 <b>{{ activeTargets.length }}</b> 条</span>
      </div>
      <el-alert v-if="error.message" type="error" :closable="false" :title="error.message" class="allocation-error"></el-alert>
      <div class="allocation-layout">
        <section class="source-panel">
          <div class="allocation-toolbar">
            <el-input v-model="search" type="textarea" :rows="2" placeholder="按原出库批次号批量搜索，支持逗号、分号、空格或换行分隔" clearable @input="clearSelection"></el-input>
            <el-checkbox v-model="onlyUnallocated" @change="clearSelection">仅看未分配（含部分未分配）</el-checkbox>
          </div>
          <el-alert v-if="unmatchedSerials.length" type="warning" :closable="false" :title="'未匹配批次号：' + unmatchedSerials.join('，')"></el-alert>
          <div class="allocation-toolbar">
            <el-button type="primary" :disabled="!selectedSources.length || !activeTargets.length" @click="openAssign">分配到成品</el-button>
            <el-button :disabled="!selectedSources.length" @click="clearSelectedAllocations">取消选中来源的分配</el-button>
            <span>已选 {{ selectedSources.length }} 条来源</span>
          </div>
          <el-table ref="sourceTable" :data="filteredSources" :row-key="item => item.plan.id" :row-class-name="sourceRowClass" border max-height="calc(100vh - 365px)" @selection-change="selectedSources = $event">
            <el-table-column type="selection" width="45"></el-table-column>
            <el-table-column label="原出库批次号／款号" min-width="165"><template #default="{ row }"><div>{{ row.plan.serialCode }}</div><div class="muted">{{ row.plan.detailCode }}</div></template></el-table-column>
            <el-table-column label="本批可处理" width="100"><template #default="{ row }">{{ capacities[row.plan.id] || 0 }}</template></el-table-column>
            <el-table-column label="已分配／剩余" width="120"><template #default="{ row }">{{ allocated(row.plan.id) }} ／ {{ remaining(row.plan.id) }}</template></el-table-column>
            <el-table-column label="分配去向" min-width="210"><template #default="{ row }">
              <div v-for="destination in destinations(row.plan.id)" :key="destination.key">{{ destination.label }}：{{ destination.quantity }}件</div>
              <span v-if="!destinations(row.plan.id).length" class="muted">未分配</span>
              <div v-if="sourceError(row.plan.id)" class="error-text">{{ sourceError(row.plan.id) }}</div>
            </template></el-table-column>
            <el-table-column label="操作" width="100"><template #default="{ row }"><el-button link type="primary" :disabled="!activeTargets.length" @click="openSplit(row)">调整分配</el-button></template></el-table-column>
          </el-table>
        </section>
        <section class="target-panel">
          <div class="allocation-toolbar"><strong>目标成品回货记录</strong><el-button type="primary" @click="addProduct">新增成品</el-button></div>
          <el-select v-if="inactiveTargets.length" v-model="restoreKey" placeholder="选择本批已有明细" style="width:100%;margin-bottom:12px;" @change="restoreTarget">
            <el-option v-for="target in inactiveTargets" :key="target.key" :value="target.key" :label="targetLabel(target)"></el-option>
          </el-select>
          <div class="target-list">
            <el-empty v-if="!activeTargets.length" description="请先新增成品或选择本批已有明细"></el-empty>
            <el-card v-for="target in activeTargets" :key="target.key" shadow="never" :class="['target-card', { 'has-error': error.targetKey === target.key }]">
              <strong>{{ targetLabel(target) }}</strong>
              <p>{{ target.originalIndex == null ? '新增记录' : '本批已有记录' }} · {{ target.row._returnMode === 'merge' ? '合并回货' : '普通回货，分配后转为合并' }}</p>
              <p>实际入库 <b>{{ target.row.stockNumber }}</b> 件 ／ 来源核销 <b>{{ consumed(target.row) }}</b> 件</p>
              <p v-if="target.row._mergeReason" class="muted">{{ target.row._mergeReason }}</p>
              <div v-if="error.targetKey === target.key" class="error-text">{{ error.message }}</div>
              <el-button link type="primary" @click="editProduct(target)">编辑成品</el-button>
              <el-button link type="danger" @click="removeTarget(target)">{{ target.originalIndex == null ? '移除成品' : '移出并还原' }}</el-button>
            </el-card>
          </div>
        </section>
      </div>
    </div>
    <template #footer><el-button :disabled="saving" @click="close">取消</el-button><el-button type="primary" :loading="saving" :disabled="!canEdit" @click="apply">应用到本批</el-button></template>

    <el-dialog v-model="showAllocation" :title="splitPlan ? '调整来源分配：' + splitPlan.plan.serialCode : '分配选中来源到成品'" width="min(950px, 96%)" append-to-body :close-on-click-modal="false">
      <template v-if="splitPlan"><el-alert type="info" :closable="false" :title="'来源款号 ' + splitPlan.plan.detailCode + '，本批可处理 ' + capacities[splitPlan.plan.id] + ' 件；填0可撤销该成品的分配。'"></el-alert></template>
      <el-form v-else label-width="100px"><el-form-item label="目标成品" required><el-select v-model="assignKey" style="width:100%"><el-option v-for="target in activeTargets" :key="target.key" :value="target.key" :label="targetLabel(target)"></el-option></el-select></el-form-item></el-form>
      <el-table :data="allocationRows" border>
        <el-table-column :label="splitPlan ? '成品回货记录' : '来源款号／批次号'" min-width="230"><template #default="{ row }">{{ row.label }}</template></el-table-column>
        <el-table-column :label="splitPlan ? '分配核销件数' : '本次增加核销件数'" width="220"><template #default="{ row }"><el-input-number v-model="row.quantity" :min="0" :max="row.max" :precision="4" :step="0.1"></el-input-number></template></el-table-column>
        <el-table-column label="校验" min-width="160"><template #default="{ row }"><span class="error-text">{{ operationErrorFor(row) }}</span></template></el-table-column>
      </el-table>
      <el-alert v-if="operationError" type="error" :closable="false" :title="operationError.message" style="margin-top:12px;"></el-alert>
      <template #footer><el-button @click="showAllocation = false">取消</el-button><el-button type="primary" :disabled="!!operationError" @click="confirmAllocation">确认分配</el-button></template>
    </el-dialog>

    <el-drawer v-model="showProduct" title="编辑成品回货记录" size="min(860px, 96%)" append-to-body :close-on-click-modal="false" :before-close="beforeProductClose">
      <template v-if="productDraft">
        <el-alert type="info" :closable="false" :title="'来源参考：克重 ' + reference(productDraft).weight + '，金重 ' + reference(productDraft).goldWeight + '，成本 ' + reference(productDraft).cost"></el-alert>
        <el-button link type="primary" :disabled="productSaving || !sources(productDraft).length" @click="useReferenceWeight(productDraft)">使用来源合计重量（按入库数量均分）</el-button>
        <repair-return-product-form :row="productDraft" :dict="dict" :number-fields="numberFields" :sku-summary="skuSummary" :disabled="productSaving" @resolve="resolveProduct" @change="changeField" @class-change="changeClass"></repair-return-product-form>
        <el-alert v-if="productError" type="error" :closable="false" :title="productError"></el-alert>
      </template>
      <template #footer><el-button :disabled="productSaving" @click="cancelProduct">取消</el-button><el-button type="primary" :loading="productSaving" :disabled="!canEdit" @click="saveProduct">保存成品草稿</el-button></template>
    </el-drawer>
  </el-dialog>
</template>

<script>
import RepairReturnProductForm from './RepairReturnProductForm.vue';
import { roundQuantity, sourcesOf, copyProduct, createAllocationDraft, allocatedQuantity, allocationErrors, addTargetAllocations, removeAllocationTarget, batchSearchTokens } from './repairBatchAllocation.mjs';

export default {
  name: 'RepairBatchMergeDialog',
  components: { RepairReturnProductForm },
  props: {
    plans: { type: Array, required: true }, rows: { type: Array, required: true },
    capacities: { type: Object, required: true }, initialPlanIds: { type: Array, default: () => [] },
    dict: { type: Object, required: true }, numberFields: { type: Array, required: true },
    makeRow: { type: Function, required: true }, resolveSku: { type: Function, required: true },
    validateProduct: { type: Function, required: true }, skuSummary: { type: Function, required: true },
    changeField: { type: Function, required: true }, changeClass: { type: Function, required: true },
    reference: { type: Function, required: true }, useReferenceWeight: { type: Function, required: true },
    canEdit: Boolean
  },
  emits: ['close', 'apply'],
  data() {
    return {
      targets: createAllocationDraft(this.rows), originalRows: this.rows.slice(), nextKey: 1,
      selectedSources: [], search: '', onlyUnallocated: false, restoreKey: '', saving: false, error: {},
      showAllocation: false, assignKey: '', splitPlan: null, allocationRows: [],
      showProduct: false, productDraft: null, editingKey: '', addingProduct: false, productSaving: false, productError: ''
    };
  },
  computed: {
    activeTargets() { return this.targets.filter(target => target.active); },
    inactiveTargets() { return this.targets.filter(target => !target.active); },
    filteredSources() {
      const tokens = batchSearchTokens(this.search);
      return this.plans.filter(item => (!tokens.length || tokens.includes(item.plan.serialCode)) && (!this.onlyUnallocated || this.remaining(item.plan.id) > 0));
    },
    unmatchedSerials() { return batchSearchTokens(this.search).filter(code => !this.plans.some(item => item.plan.serialCode === code)); },
    summary() {
      const capacity = Object.values(this.capacities).reduce((sum, value) => sum + Number(value), 0);
      const allocated = Object.keys(this.capacities).reduce((sum, id) => sum + this.allocated(id), 0);
      return { capacity: roundQuantity(capacity), allocated: roundQuantity(allocated), remaining: roundQuantity(capacity - allocated) };
    },
    liveErrors() { return allocationErrors(this.targets, this.capacities); },
    operationError() {
      if (!this.showAllocation) return null;
      for (const row of this.allocationRows) {
        if (!Number.isFinite(Number(row.quantity)) || Number(row.quantity) < 0 || row.quantity == null || String(row.quantity).trim() === '') return { planID: row.planID, targetKey: row.targetKey, message: '核销件数须为大于等于0的有效数字；填0表示不分配' };
        if (roundQuantity(row.quantity) !== Number(row.quantity)) return { planID: row.planID, targetKey: row.targetKey, message: '核销件数最多支持4位小数' };
      }
      const proposed = this.proposedAllocation();
      return proposed.error || allocationErrors(proposed.targets, this.capacities)[0] || null;
    }
  },
  mounted() {
    this.$nextTick(() => {
      for (const item of this.filteredSources) {
        if (this.initialPlanIds.includes(item.plan.id)) this.$refs.sourceTable?.toggleRowSelection(item, true);
      }
    });
  },
  methods: {
    sources: sourcesOf,
    consumed(row) { return roundQuantity(sourcesOf(row).reduce((sum, source) => sum + Number(source.consumedQuantity), 0)); },
    allocated(planID) { return allocatedQuantity(this.targets, planID); },
    remaining(planID) { return roundQuantity(Number(this.capacities[planID] || 0) - this.allocated(planID)); },
    targetLabel(target) {
      const index = target.originalIndex == null ? `新增${target.key.slice(4)}` : `本批${target.originalIndex + 1}`;
      return `${index} · ${target.row.detailCode || '待填款号'} · ${target.row.serialCode || '待填回货批次号'}`;
    },
    destinations(planID) {
      return this.targets.flatMap(target => sourcesOf(target.row).filter(source => source.planTempID === planID)
        .map(source => ({ key: target.key, label: this.targetLabel(target) + (target.active ? '' : '（本批保留）'), quantity: source.consumedQuantity })));
    },
    sourceError(planID) { return this.liveErrors.find(item => item.planID === planID)?.message || (this.error.planID === planID ? this.error.message : ''); },
    sourceRowClass({ row }) { return this.sourceError(row.plan.id) ? 'allocation-source-error' : ''; },
    clearSelection() { this.$refs.sourceTable?.clearSelection(); this.selectedSources = []; },
    restoreTarget(key) { const target = this.targets.find(item => item.key === key); if (target) target.active = true; this.restoreKey = ''; this.error = {}; },
    removeTarget(target) { this.error = removeAllocationTarget(this.targets, target.key, this.capacities) || {}; },
    addProduct() {
      const row = { ...this.makeRow(), _returnMode: 'merge', _mergeReason: '', _sources: [], stockNumber: 1, stockRemain: 1 };
      const target = { key: `new-${this.nextKey++}`, originalIndex: null, row, active: true, touched: true };
      this.targets.push(target);
      this.editProduct(target);
      this.addingProduct = true;
    },
    editProduct(target) {
      this.editingKey = target.key;
      this.addingProduct = false;
      this.productDraft = { ...copyProduct(target.row), _returnMode: 'merge' };
      this.productError = '';
      this.showProduct = true;
    },
    async resolveProduct(row) { await this.resolveSku(row, true, true); },
    cancelProduct() {
      if (this.productSaving) return;
      if (this.addingProduct) this.targets = this.targets.filter(target => target.key !== this.editingKey);
      this.showProduct = false;
    },
    async saveProduct() {
      if (this.productSaving || !this.canEdit) return;
      this.productSaving = true;
      const row = this.productDraft;
      try {
        if (!await this.resolveSku(row, true)) return;
        if (!this.showProduct || this.productDraft !== row || !this.canEdit) return;
        this.productError = this.validateProduct(row) || this.reasonError(row);
        if (this.productError) return;
        const target = this.targets.find(item => item.key === this.editingKey);
        target.row = copyProduct(row);
        target.touched = true;
        this.error = {};
        this.showProduct = false;
      } catch (error) { this.productError = error.message || '成品校验失败，请重试'; }
      finally { this.productSaving = false; }
    },
    reasonError(row) {
      if (!(row._mergeReason || '').trim()) return '请填写合并说明';
      return row._mergeReason.trim().length > 500 ? '合并说明不能超过500字' : '';
    },
    openAssign() {
      this.splitPlan = null;
      this.assignKey = this.activeTargets.some(item => item.key === this.assignKey) ? this.assignKey : this.activeTargets[0]?.key || '';
      this.allocationRows = this.selectedSources.map(item => ({ planID: item.plan.id, label: `${item.plan.detailCode} / ${item.plan.serialCode}`, quantity: Math.max(0, this.remaining(item.plan.id)), max: Math.max(0, this.remaining(item.plan.id)) }));
      this.showAllocation = true;
    },
    openSplit(item) {
      this.splitPlan = item;
      this.allocationRows = this.activeTargets.map(target => ({ targetKey: target.key, planID: item.plan.id, label: this.targetLabel(target), quantity: sourcesOf(target.row).find(source => source.planTempID === item.plan.id)?.consumedQuantity || 0, max: Number(this.capacities[item.plan.id] || 0) }));
      this.showAllocation = true;
    },
    proposedAllocation() {
      const targets = this.targets.map(target => ({ ...target, row: copyProduct(target.row) }));
      if (this.splitPlan) {
        const id = this.splitPlan.plan.id;
        for (const edit of this.allocationRows) {
          const target = targets.find(item => item.key === edit.targetKey);
          const sources = sourcesOf(target.row).filter(source => source.planTempID !== id);
          if (Number(edit.quantity) > 0) sources.push({ planTempID: id, consumedQuantity: Number(edit.quantity) });
          const old = sourcesOf(target.row).find(source => source.planTempID === id)?.consumedQuantity || 0;
          if (Number(old) !== Number(edit.quantity)) {
            target.row = { ...target.row, _returnMode: 'merge', _sources: sources };
            target.touched = true;
          }
        }
        return { targets };
      }
      const additions = this.allocationRows.filter(row => Number(row.quantity) > 0).map(row => ({ planTempID: row.planID, consumedQuantity: Number(row.quantity) }));
      if (!additions.length) return { targets, error: { message: '请至少为一个来源填写大于0的核销件数' } };
      if (!this.assignKey) return { targets, error: { message: '请选择目标成品' } };
      return { targets, error: addTargetAllocations(targets, this.assignKey, additions, this.capacities) };
    },
    operationErrorFor(row) {
      const error = this.operationError;
      return error && (this.splitPlan ? !error.targetKey || error.targetKey === row.targetKey : error.planID === row.planID) ? error.message : '';
    },
    confirmAllocation() {
      if (this.operationError) return;
      this.targets = this.proposedAllocation().targets;
      this.error = {};
      this.showAllocation = false;
      this.clearSelection();
    },
    clearSelectedAllocations() {
      const ids = new Set(this.selectedSources.map(item => item.plan.id));
      const proposed = this.targets.map(target => {
        const sources = sourcesOf(target.row);
        if (!target.active || !sources.some(source => ids.has(source.planTempID))) return target;
        return { ...target, touched: true, row: { ...target.row, _returnMode: 'merge', _sources: sources.filter(source => !ids.has(source.planTempID)) } };
      });
      this.targets = proposed;
      this.error = {};
      this.clearSelection();
    },
    async apply() {
      if (this.saving || !this.canEdit) return;
      this.error = allocationErrors(this.targets, this.capacities, true)[0] || {};
      if (this.error.message) { this.revealError(); return; }
      this.saving = true;
      try {
        for (const target of this.targets.filter(item => item.touched || item.originalIndex == null)) {
          if (!await this.resolveSku(target.row, true)) { this.error = { targetKey: target.key, message: target.row._skuError || '请完成成品款号匹配' }; return; }
          if (!this.canEdit) return;
          const message = this.validateProduct(target.row) || this.reasonError(target.row);
          if (message) { this.error = { targetKey: target.key, message }; return; }
        }
        this.error = allocationErrors(this.targets, this.capacities, true)[0] || {};
        if (this.error.message || !this.canEdit) return;
        this.$emit('apply', { originalRows: this.originalRows, targets: this.targets });
      } catch (error) { this.error = { message: error.message || '应用失败，请重试' }; }
      finally { this.saving = false; this.revealError(); }
    },
    revealError() {
      if (this.error.planID) { this.search = ''; this.onlyUnallocated = false; this.clearSelection(); }
      this.$nextTick(() => {
        this.$refs.bulkContent?.querySelector('.allocation-source-error, .target-card.has-error')?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      });
    },
    beforeProductClose(done) { if (!this.productSaving) { this.cancelProduct(); done(); } },
    beforeClose(done) { if (!this.saving && !this.productSaving) done(); },
    close() { if (!this.saving && !this.productSaving) this.$emit('close'); }
  }
};
</script>

<style scoped>
:global(.repair-bulk-merge-dialog.is-fullscreen) { display:flex; flex-direction:column; }
:global(.repair-bulk-merge-dialog > .el-dialog__body) { flex:1; min-height:0; overflow:auto; }
:global(.repair-bulk-merge-dialog > .el-dialog__header),
:global(.repair-bulk-merge-dialog > .el-dialog__footer) { flex-shrink:0; }
:global(.repair-bulk-merge-dialog > .el-dialog__footer) { border-top:1px solid var(--el-border-color-light); padding-top:12px; }
.bulk-merge { min-height:0; }
.allocation-summary { display:flex; flex-wrap:wrap; gap:24px; padding:18px 0; font-size:15px; }
.allocation-summary b { color:var(--el-color-primary); }
.allocation-layout { display:grid; grid-template-columns:minmax(0, 2fr) minmax(320px, 1fr); gap:20px; }
.allocation-toolbar { display:flex; flex-wrap:wrap; gap:10px; align-items:center; margin-bottom:12px; }
.target-list { max-height:calc(100vh - 285px); overflow:auto; }
.target-card { margin-bottom:12px; overflow-wrap:anywhere; }
.target-card p { margin:10px 0; }
.muted { color:var(--el-text-color-secondary); font-size:12px; }
.error-text { color:var(--el-color-danger); line-height:1.5; }
.allocation-error { margin-bottom:12px; }
.has-error { border-color:var(--el-color-danger); }
:deep(.allocation-source-error) { --el-table-tr-bg-color:var(--el-color-danger-light-9); }
@media (max-width: 1050px) { .allocation-layout { grid-template-columns:1fr; } .target-list { max-height:400px; } }
</style>
